import { IAddressEntity } from '../Services/api/addresses/addresses.interface'
import { ICustomerEntity } from '../Services/api/customers/customers.interface'
import { ListOrdersUseCase } from '../Usecase/Orders/list-orders-usecase'
import { IOrderEntity } from '../Services/api/orders/orders.interface'

type ApiOrder = IOrderEntity & Record<string, unknown>

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return String(value)
    }
  }
  return ''
}

function normalizeCustomer(raw?: unknown): ICustomerEntity | undefined {
  if (!raw || typeof raw !== 'object') return undefined

  const customer = raw as Record<string, unknown>
  const name = pickString(customer.name, customer.companyName, customer.company_name)

  if (!name && !customer.email && !customer.id) {
    return undefined
  }

  return {
    ...(raw as ICustomerEntity),
    id: pickString(customer.id),
    name,
    email: pickString(customer.email),
    document: pickString(customer.document),
    phone: pickString(customer.phone),
    personType: (customer.personType || customer.person_type || 'PF') as ICustomerEntity['personType'],
    stateRegistration: pickString(customer.stateRegistration, customer.state_registration),
    companyName: pickString(customer.companyName, customer.company_name) || undefined,
    addresses: Array.isArray(customer.addresses) ? customer.addresses : [],
  }
}

function normalizeAddress(raw?: unknown): IAddressEntity | undefined {
  if (!raw || typeof raw !== 'object') return undefined

  const address = raw as Record<string, unknown>

  return {
    id: pickString(address.id),
    name: pickString(address.name),
    number: pickString(address.number),
    locality: pickString(address.locality),
    city: pickString(address.city),
    province: pickString(address.province),
    zipcode: pickString(address.zipcode, address.zip_code),
  } as IAddressEntity
}

export function hasOrderAddressContent(address?: Partial<IAddressEntity> | null): boolean {
  if (!address) return false
  return Boolean(address.id || address.name || address.zipcode || address.city)
}

function hasCustomerContent(customer?: ICustomerEntity): boolean {
  if (!customer) return false
  return Boolean(customer.name || customer.email || customer.companyName)
}

/** Normaliza campos da API (camelCase + snake_case) sem calcular valores. */
export function normalizeOrderApiResponse(raw: IOrderEntity): IOrderEntity {
  const order = raw as ApiOrder

  return {
    ...raw,
    totalValue: pickString(order.totalValue, order.total_value),
    originalValue: pickString(order.originalValue, order.original_value),
    discountValue: pickString(order.discountValue, order.discount_value),
    nsOrderStringified: pickString(order.nsOrderStringified, order.ns_order_stringified) || undefined,
    orderStringified: pickString(order.orderStringified, order.order_stringified) || order.orderStringified,
    nsPaymentGatewayName: pickString(order.nsPaymentGatewayName, order.ns_payment_gateway_name) || undefined,
    nsPaymentGateway: pickString(order.nsPaymentGateway, order.ns_payment_gateway) || undefined,
    nsShippingCost: pickString(order.nsShippingCost, order.ns_shipping_cost) || undefined,
    customer: normalizeCustomer(order.customer),
    shippingAddress: normalizeAddress(order.shippingAddress ?? order.shipping_address),
    billingAddress: normalizeAddress(order.billingAddress ?? order.billing_address),
    orderPayment: (order.orderPayment ?? order.order_payment) as IOrderEntity['orderPayment'],
    omieBillingCompanyId: pickString(order.omieBillingCompanyId, order.omie_billing_company_id) || undefined,
    omieBillingCompany: (order.omieBillingCompany ?? order.omie_billing_company) as IOrderEntity['omieBillingCompany'],
    omieReceivingCompanyId: order.omieReceivingCompanyId !== undefined
      ? order.omieReceivingCompanyId as string | null
      : order.omie_receiving_company_id as string | null | undefined,
    omieReceivingCompany: (order.omieReceivingCompany ?? order.omie_receiving_company) as IOrderEntity['omieReceivingCompany'],
    products: Array.isArray(order.products) ? order.products : [],
  }
}

function isUsableNsOrderJson(raw?: string | null): boolean {
  if (!raw || raw === '{}') return false
  if (raw.endsWith('...') && raw.length < 80) return false
  return true
}

function mergeOrderPreferFilled(primary: IOrderEntity, secondary: IOrderEntity): IOrderEntity {
  const primaryNs = isUsableNsOrderJson(primary.nsOrderStringified)
    ? primary.nsOrderStringified
    : isUsableNsOrderJson(secondary.nsOrderStringified)
      ? secondary.nsOrderStringified
      : primary.nsOrderStringified

  return normalizeOrderApiResponse({
    ...secondary,
    ...primary,
    id: primary.id,
    customer: hasCustomerContent(primary.customer) ? primary.customer : secondary.customer ?? primary.customer,
    products: (primary.products?.length ?? 0) > 0 ? primary.products : secondary.products ?? [],
    totalValue: Number(primary.totalValue) > 0 ? primary.totalValue : secondary.totalValue,
    originalValue: Number(primary.originalValue) > 0 ? primary.originalValue : secondary.originalValue,
    discountValue: primary.discountValue || secondary.discountValue,
    nsShippingCost: primary.nsShippingCost || secondary.nsShippingCost,
    nsPaymentGatewayName: primary.nsPaymentGatewayName || secondary.nsPaymentGatewayName,
    nsPaymentGateway: primary.nsPaymentGateway || secondary.nsPaymentGateway,
    shippingAddress: hasOrderAddressContent(primary.shippingAddress)
      ? primary.shippingAddress
      : secondary.shippingAddress ?? primary.shippingAddress,
    billingAddress: hasOrderAddressContent(primary.billingAddress)
      ? primary.billingAddress
      : secondary.billingAddress ?? primary.billingAddress,
    orderPayment: primary.orderPayment ?? secondary.orderPayment,
    nsOrderStringified: primaryNs,
    orderStringified: primary.orderStringified || secondary.orderStringified,
  })
}

export function isOrderDetailsIncomplete(order?: IOrderEntity): boolean {
  if (!order?.id) return true

  const hasCustomer = hasCustomerContent(order.customer)
  const hasProducts = (order.products?.length ?? 0) > 0
  const hasTotal = Number(order.totalValue) > 0

  return !hasCustomer || !hasProducts || !hasTotal
}

export async function enrichOrderFromList(
  order: IOrderEntity,
  listOrdersUseCase: ListOrdersUseCase,
): Promise<IOrderEntity> {
  const normalized = normalizeOrderApiResponse(order)

  if (!isOrderDetailsIncomplete(normalized) || !normalized.nsOrderId) {
    return normalized
  }

  const listResult = await listOrdersUseCase.handle({
    page: 1,
    offset: 5,
    searchBy: String(normalized.nsOrderId),
  })

  if (listResult.isFailure) {
    return normalized
  }

  const listData = listResult.getValue()?.data ?? []
  const fromList =
    listData.find(item => item.id === normalized.id) ??
    listData.find(item => item.nsOrderId === normalized.nsOrderId) ??
    listData[0]

  if (!fromList) {
    return normalized
  }

  return mergeOrderPreferFilled(normalized, normalizeOrderApiResponse(fromList))
}
